import React from "react";
import { useMappedState } from "redux-react-hook";
import { toast } from "react-toastify";
import styled from "styled-components";

import { Table } from "src/library/components/Tables";
import { ITableInstance } from "src/library/components/Tables/Table";
import { Text } from "src/library/components/Text";
import Button from "src/library/components/Button/Button";
import { IOrder } from "src/core/orders/api/orders.api";
import {
  FilterParams,
  PaginatedParams
} from "src/core/shared/api";
import {
  IProductSubstitution,
  useSubstitutionsApi
} from "src/modules/substitutions/api/substitutions.api";
import { adminOrderTableColumns } from "src/core/shared/components/tables";
import { PageProps } from "src/pages/Router/routes";
import { IStore } from "src/core/shared/store";
import { OrderActionsList } from "src/core/orders/components/OrderActionsList";

import { ProductSubstitutionPageProps } from ".";

interface AffectedOrdersTabProps {
  substitution: IProductSubstitution;
}

const defaultPagination: PaginatedParams<IOrder> = {
  page: 0,
  offset: 0,
  limit: 10,
  sort: [ [ "id", "DESC" ] ]
};

export const AffectedOrdersTab: React.ReactFCWithChildren<PageProps<AffectedOrdersTabProps, ProductSubstitutionPageProps>> = ({
  substitution,
  history,
  terminology
}) => {
  const [ getOrdersBySubstitutionResponse, getOrdersBySubstitutionRequest ] = useSubstitutionsApi("LIST_ORDERS_BY_SUBSTITUTION");
  const [ downloadReportRes, downloadReportReq ] = useSubstitutionsApi("DOWNLOAD_REPORT");
  const TableRef = React.useRef<ITableInstance<IOrder>>(null);

  const onFetchData = React.useCallback((
    filters: FilterParams<IOrder>,
    pagination: PaginatedParams<IOrder>
  ) => {
    getOrdersBySubstitutionRequest({
      substitutionId: substitution.id,
      filters,
      pagination
    });
  }, [ getOrdersBySubstitutionRequest, substitution.id ]);

  const viewOrder = (id: number) => {
    history.push(`/orders/${id}`);
  };

  const reload = React.useCallback(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ TableRef ]);

  const {
    orderStatuses, role, erp, installedModules
  } = useMappedState((store: IStore) => ({
    orderStatuses: store.global.settings ? store.global.settings.orderStatuses : [],
    role: store.global.role,
    erp: store.global.settings && store.global.settings.erp.name,
    installedModules: store.global.settings?.installedModules || []
  }));

  const tableColumns = React.useMemo(() => adminOrderTableColumns({
    terminology,
    orderStatuses,
    installedModules,
    ActionButton: row => erp && role ? (
      <OrderActionsList
        erp={erp}
        terminology={terminology}
        role={role}
        order={row.original}
        reload={reload}
      />
    ) : null
  }), [
    terminology,
    orderStatuses,
    erp,
    role,
    reload
  ]);

  React.useEffect(() => {
    if (downloadReportRes.data) {
      window.open(downloadReportRes.data.url, "blank");
      downloadReportRes.data = null;
    } else if (downloadReportRes.error) {
      toast.error(downloadReportRes.error.message);
    }
  }, [ downloadReportRes ]);

  const downloadReport = React.useCallback((substitutionId: number) => {
    downloadReportReq({ substitutionId });
  }, []);

  return (
    <div>
      <TabHeader>
        <Text color="secondary">
          Below are all of the orders affected by this product substitution
        </Text>

        <Button
          loading={downloadReportRes.pending}
          onClick={() => downloadReport(substitution.id)}
        >
          Export
        </Button>
      </TabHeader>

      <Table
        tableId="global"
        ref={TableRef}
        defaultPagination={defaultPagination}
        mainAction={row => viewOrder(row.original.id)}
        error={getOrdersBySubstitutionResponse.error}
        data={getOrdersBySubstitutionResponse.data?.result || []}
        onFetchData={onFetchData}
        pages={getOrdersBySubstitutionResponse.data?.pages || 0}
        totalRows={getOrdersBySubstitutionResponse.data?.count || 0}
        tableOf={terminology.order.plural.text}
        loading={getOrdersBySubstitutionResponse.pending}
        columns={tableColumns}
      />
    </div>
  );
};

const TabHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
`;
